import { css } from 'lit';

export default css`
  :host {
    display: inline-block;
    font-family: var(--font-family-base, 'Inter, Arial, sans-serif');
    box-sizing: border-box;
  }

  button {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    border: 0;
    border-radius: var(--button-radius, var(--radius-md, 12px));
    background: transparent;
    gap: 5px;
    padding: var(--spacing-md, 16px) var(--spacing-lg, 24px);
    width: 100%;
    max-width: 300px;
    font-family: inherit;
    border-left-width: var(--button-border-left, var(--sm-line, 1.5px));
    border-right-width: var(--button-border-right, var(--sm-line, 1.5px));
    border-top-width: var(--sm-line, 1.5px);
    border-bottom-width: var(--sm-line, 1.5px);
    transition: all 0.2s ease, transform 0.1s ease;
    font-weight: var(--font-weight-semibold, 600);
    color: var(--button-text-color, inherit);
    background-color: var(--button-bg-color, transparent);
    border-color: var(--button-outline-color, transparent);
  }

  button:focus-visible {
    outline: 2px solid var(--color-secondary, #2594e3);
    outline-offset: 2px;
    box-shadow: var(--shadow-focus);
  }

  /* Press animation for all style types */
  :host([state="press"]) button,
  :host button:active {
    transform: var(--scale-hover);
    box-shadow: var(--shadow-focus);
  }

  /* Disable focus styling when inside studio-button-group */
  :where(studio-button-group) :host button:focus {
    border-color: inherit;
    box-shadow: none;
  }

  /* Border control for button group */
  :host([position="left"]) button {
    --button-border-right: 0;
    border-right-width: 0;
    --button-border-left: var(--sm-line, 1.5px);
    border-left-width: var(--sm-line, 1.5px);
  }

  :host([position="center"]) button {
    --button-border-right: 0;
    border-right-width: 0;
    --button-border-left: var(--sm-line, 1.5px);
    border-left-width: var(--sm-line, 1.5px);
  }

  :host([position="right"]) button {
    --button-border-right: var(--sm-line, 1.5px);
    border-right-width: var(--sm-line, 1.5px);
    --button-border-left: var(--sm-line, 1.5px);
    border-left-width: var(--sm-line, 1.5px);
  }

  /* Sizes */
  :host([size="giant"]) button {
    height: var(--height-button-giant);
    min-width: var(--height-button-giant);
    padding: var(--spacing-md, 16px) var(--spacing-lg);
    font-size: var(--font-size-button-giant);
    line-height: var(--line-height-button-giant);
  }

  :host([size="large"]) button {
    height: var(--height-button-large);
    min-width: var(--height-button-large);
    padding: var(--spacing-sm, 14px) var(--spacing-md);
    font-size: var(--font-size-button-large);
    line-height: var(--line-height-button-large);
  }

  :host([size="medium"]) button {
    height: var(--height-button-medium);
    min-width: var(--height-button-large);
    padding: var(--spacing-xs, 12px) var(--spacing-sm);
    font-size: var(--font-size-button-medium);
    line-height: var(--line-height-button-medium);
  }

  :host([size="small"]) button {
    height: var(--height-button-small);
    min-width: var(--height-button-small);
    padding: calc(var(--spacing-xs, 8px) / 2) var(--spacing-sm);
    font-size: var(--font-size-button-small);
    line-height: var(--line-height-button-small);
  }

  :host([size="tiny"]) button {
    height: var(--height-button-tiny);
    min-width: var(--height-button-tiny);
    padding: calc(var(--spacing-xs, 6px) / 2) var(--spacing-x);
    border-radius: var(--radius-s);
    font-size: var(--font-size-button-tiny);
    line-height: var(--line-height-button-tiny);
  }

  /* Icon-only sizes */
  :host([icon-only][size="giant"]) button {
    height: var(--height-button-giant);
    padding: var(--spacing-md);
  }

  :host([icon-only][size="large"]) button {
    height: var(--height-button-large);
    padding: var(--spacing-sm);
  }

  :host([icon-only][size="medium"]) button {
    height: var(--height-button-medium);
    padding: var(--spacing-xs);
  }

  :host([icon-only][size="small"]) button {
    height: var(--height-button-small);
    padding: calc(var(--spacing-xs) / 2);
  }

  :host([icon-only][size="tiny"]) button {
    height: var(--height-button-tiny);
    padding: calc(var(--spacing-xs) / 2);
  }

  /* Style states for Filled type */
  :host([style-type="filled"]) button,
  :host([style-type="filled"][state="default"]) button {
    color: var(--button-text-color, var(--color-white));
    background-color: var(--button-bg-color, var(--color-primary));
    border-color: var(--button-outline-color, var(--color-hover));
    border-left-width: var(--button-border-left, var(--sm-line));
    border-right-width: var(--button-border-right, var(--sm-line));
    border-top-width: var(--sm-line);
    border-bottom-width: var(--sm-line);
  }

  :host([style-type="filled"][state="hover"]) button,
  :host([style-type="filled"]) button:hover {
    background-color: var(--button-bg-color, var(--color-secondary));
  }

  :host([style-type="filled"][state="focus"]) button,
  :host([style-type="filled"]) button:focus {
    border-color: var(--button-outline-color, var(--color-primary));
    border-left-width: var(--button-border-left, var(--sm-line));
    border-right-width: var(--button-border-right, var(--sm-line));
    border-top-width: var(--sm-line);
    border-bottom-width: var(--sm-line);
    border-style: solid;
    background-color: var(--button-bg-color, var(--color-secondary));
    box-shadow: var(--shadow-focus);
  }

  :host([style-type="filled"][state="press"]) button,
  :host([style-type="filled"]) button:active {
    border-color: var(--button-outline-color, var(--color-primary-dark));
    border-left-width: var(--button-border-left, var(--sm-line));
    border-right-width: var(--button-border-right, var(--sm-line));
    border-top-width: var(--sm-line);
    border-bottom-width: var(--sm-line);
    border-style: solid;
    transform: scale(0.95);
    background-color: var(--button-bg-color, var(--color-primary-dark));
    box-shadow: var(--shadow-focus);
  }

  :host([style-type="filled"][state="disabled"]) button,
  :host([style-type="filled"]) button:disabled {
    background-color: var(--button-bg-color, var(--color-gray-3));
    color: var(--button-text-color, var(--color-gray-5));
  }

  /* Style states for Menu type */
  :host([style-type="menu"]) button,
  :host([style-type="menu"][state="default"]) button {
    color: var(--button-text-color, var(--color-gray-9));
    background-color: var(--button-bg-color, transparent);
    text-align: left;
    justify-content: flex-start;
  }

  :host([style-type="menu"][state="hover"]) button,
  :host([style-type="menu"]) button:hover {
    background-color: var(--button-bg-color, var(--color-white));
  }

  :host([style-type="menu"][state="focus"]) button,
  :host([style-type="menu"]) button:focus {
    background-color: var(--button-bg-color, var(--color-white));
    box-shadow: 0 0 0 3px var(--color-secondary);
  }

  :host([style-type="menu"][state="press"]) button,
  :host([style-type="menu"]) button:active {
    background-color: var(--button-bg-color, var(--color-white));
    box-shadow: var(--shadow-focus);
  }

  :host([style-type="menu"][state="disabled"]) button,
  :host([style-type="menu"]) button:disabled {
    background-color: var(--button-bg-color, var(--color-white));
    color: var(--button-text-color, var(--color-gray-5));
  }

  /* Style states for Outline type */
  :host([style-type="outline"]) button,
  :host([style-type="outline"][state="default"]) button {
    color: var(--button-text-color, var(--color-primary-dark));
    background-color: var(--button-bg-color, transparent);
    border-color: var(--button-outline-color, var(--color-primary-dark));
    border-left-width: var(--button-border-left, var(--sm-line));
    border-right-width: var(--button-border-right, var(--sm-line));
    border-top-width: var(--sm-line);
    border-bottom-width: var(--sm-line);
    border-style: solid;
  }

  :host([style-type="outline"][state="hover"]) button,
  :host([style-type="outline"]) button:hover {
    background-color: var(--button-bg-color, var(--color-hover));
  }

  :host([style-type="outline"][state="focus"]) button,
  :host([style-type="outline"]) button:focus {
    background-color: var(--button-bg-color, transparent);
    border-left-width: var(--button-border-left, var(--sm-line));
    border-right-width: var(--button-border-right, var(--sm-line));
    border-top-width: var(--sm-line);
    border-bottom-width: var(--sm-line);
    border-style: solid;
  }

  :host([style-type="outline"][state="press"]) button,
  :host([style-type="outline"]) button:active {
    border-color: var(--button-outline-color, var(--color-hover));
    border-left-width: var(--button-border-left, var(--md-line));
    border-right-width: var(--button-border-right, var(--md-line));
    border-top-width: var(--md-line);
    border-bottom-width: var(--md-line);
    border-style: solid;
    background-color: var(--button-bg-color, transparent);
    transform: scale(0.95);
    box-shadow: var(--shadow-focus);
  }

  :host([style-type="outline"][state="disabled"]) button,
  :host([style-type="outline"]) button:disabled {
    background-color: var(--button-bg-color, var(--color-gray-3));
    color: var(--button-text-color, var(--color-gray-5));
    border-color: var(--button-outline-color, var(--color-gray-5));
  }

  /* Style states for Clear type */
  :host([style-type="clear"]) button,
  :host([style-type="clear"][state="default"]) button {
    color: var(--button-text-color, var(--color-primary-dark));
    border: var(--sm-line, 1.5px) solid var(--button-outline-color, transparent);
    background-color: var(--button-bg-color, transparent);
  }

  :host([style-type="clear"][state="hover"]) button,
  :host([style-type="clear"]) button:hover {
    color: var(--button-text-color, var(--color-secondary));
  }

  :host([style-type="clear"][state="focus"]) button,
  :host([style-type="clear"]) button:focus {
    background-color: var(--button-bg-color, transparent);
    border-color: var(--button-outline-color, var(--color-secondary));
    border-left-width: var(--button-border-left, var(--sm-line));
    border-right-width: var(--button-border-right, var(--sm-line));
    border-top-width: var(--sm-line);
    border-bottom-width: var(--sm-line);
    border-style: solid;
  }

  :host([style-type="clear"][state="press"]) button,
  :host([style-type="clear"]) button:active {
    border-color: var(--button-outline-color, var(--color-hover));
    border-left-width: var(--button-border-left, var(--md-line));
    border-right-width: var(--button-border-right, var(--md-line));
    border-top-width: var(--md-line);
    border-bottom-width: var(--md-line);
    border-style: solid;
    background-color: var(--button-bg-color, transparent);
    transform: scale(0.95);
    box-shadow: var(--shadow-focus);
  }

  :host([style-type="clear"][state="disabled"]) button,
  :host([style-type="clear"]) button:disabled {
    color: var(--button-text-color, var(--color-gray-5));
    background-color: var(--button-bg-color, transparent);
  }

  :host([state="disabled"]) button {
    cursor: not-allowed;
    opacity: 0.7;
  }

  /* Icon */
  ::slotted([slot="icon"]) {
    width: var(--icon-size);
    height: var(--icon-size);
    flex: none;
    order: 0;
    margin-right: var(--spacing-xs);
  }

  :host([style-type="filled"]) ::slotted([slot="icon"]) {
    color: var(--button-text-color, var(--color-white));
  }

  :host([style-type="outline"]) ::slotted([slot="icon"]) {
    color: var(--button-text-color, var(--color-black));
  }

  :host([style-type="clear"]) ::slotted([slot="icon"]) {
    color: var(--button-text-color, var(--color-black));
  }

  :host([state="focus"][style-type="outline"]) ::slotted([slot="icon"]),
  :host([state="focus"][style-type="clear"]) ::slotted([slot="icon"]) {
    color: var(--button-text-color, var(--color-secondary));
  }

  :host([state="disabled"]) ::slotted([slot="icon"]) {
    color: var(--button-text-color, var(--color-gray-5));
  }

  /* Text */
  ::slotted([slot="text"]) {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    padding: 0 var(--spacing-xs);
    order: 1;
    font-size: inherit;
    line-height: inherit;
    font-weight: var(--font-weight-semibold);
    font-family: inherit;
  }

  :host([style-type="filled"]) ::slotted([slot="text"]) {
    color: var(--button-text-color, var(--color-white));
  }

  :host([style-type="outline"]) ::slotted([slot="text"]) {
     color: var(--button-text-color, var(--color-primary-dark));
  }

  :host([style-type="clear"]) ::slotted([slot="text"]) {
     color: var(--button-text-color, var(--color-secondary));
  }

  :host([state="focus"][style-type="outline"]) ::slotted([slot="text"]),
  :host([state="focus"][style-type="clear"]) ::slotted([slot="text"]) {
    color: var(--button-text-color, var(--color-secondary));
  }

  :host([state="disabled"]) ::slotted([slot="text"]) {
    color: var(--button-text-color, var(--color-gray-5));
  }

  @media (max-width: 768px) {

    button {
      max-width: unset;
    }
  }
`;